import React, { useCallback, useEffect, useState } from 'react';
import { MagnifyingGlassIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { trainingTemplatesAPI } from '../../services/api';
import type { TrainingTemplate } from '../../types/trainingTemplate';
import { useToast } from '../../contexts/ToastContext';
import DeleteConfirmModal from '../ui/DeleteConfirmModal';
import PlanView from '../trainings/PlanView';
import DrillSheet from './DrillSheet';
import DrillThumbnail from './DrillThumbnail';
import TemplateFormModal from './TemplateFormModal';
import { getErrorMessage } from '../../utils/errorMessage';

/** Az edzéssablonok listája a Gyakorlattár oldalon: keresés, megtekintés, szerkesztés, törlés. */
const TemplatesTab: React.FC = () => {
  const { showToast } = useToast();
  const [templates, setTemplates] = useState<TrainingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);

  const [viewing, setViewing] = useState<TrainingTemplate | null>(null);
  const [editing, setEditing] = useState<TrainingTemplate | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState<TrainingTemplate | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates(await trainingTemplatesAPI.getAll({ search: search || undefined, onlyMine: onlyMine || undefined }));
    } catch (err) {
      console.error('Az edzéssablonok betöltése nem sikerült:', err);
    } finally {
      setLoading(false);
    }
  }, [search, onlyMine]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await trainingTemplatesAPI.delete(deleting.id);
      showToast('Sablon törölve.');
      setDeleting(null);
      setViewing(null);
      load();
    } catch (err: any) {
      showToast(getErrorMessage(err, 'A törlés nem sikerült.'), 'error');
    }
  };

  return (
    <div>
      <div className="mb-6 rounded-lg bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Keresés név vagy cél alapján..."
              aria-label="Keresés az edzéssablonok között"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={onlyMine}
              onChange={(e) => setOnlyMine(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Csak a saját sablonjaim
          </label>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <PlusIcon className="mr-1.5 h-4 w-4" />
            Új sablon
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center text-gray-500">Betöltés...</div>
      ) : templates.length === 0 ? (
        <div className="rounded-lg bg-white py-12 text-center shadow">
          <p className="text-gray-500">{search || onlyMine ? 'Nincs a szűrésnek megfelelő sablon.' : 'Még nincs edzéssablon.'}</p>
          <p className="mt-1 text-sm text-gray-400">
            Egy kész edzéstervnél a „Mentés sablonként” gombbal, vagy itt az „Új sablon” gombbal készíthetsz egyet.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {templates.map((template) => {
            const firstDiagram = template.items.find((i) => i.diagram)?.diagram;
            return (
              <button
                key={template.id}
                type="button"
                onClick={() => setViewing(template)}
                className="flex flex-col overflow-hidden rounded-lg bg-white text-left shadow transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <DrillThumbnail diagram={firstDiagram} title={template.name} className="rounded-none rounded-t-lg" />
                <div className="flex flex-1 flex-col gap-1 p-3">
                  <p className="font-medium text-gray-900 line-clamp-2">{template.name}</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800">
                      {template.items.length} blokk
                    </span>
                    {template.totalMinutes > 0 && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{template.totalMinutes} perc</span>
                    )}
                    {template.ageGroupHint && <span className="text-xs text-gray-500">{template.ageGroupHint}</span>}
                  </div>
                  {template.goal && <p className="text-xs text-gray-500 line-clamp-2">{template.goal}</p>}
                  <p className="mt-auto pt-1 text-xs text-gray-400">{template.createdByName}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {viewing && (
        <DrillSheet
          title={viewing.name}
          onClose={() => setViewing(null)}
          maxWidthClass="md:max-w-3xl"
          footer={
            viewing.canModify ? (
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setDeleting(viewing)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md border border-red-200 px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4" />
                  Törlés
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(viewing);
                    setViewing(null);
                    setFormOpen(true);
                  }}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
                >
                  <PencilIcon className="h-4 w-4" />
                  Szerkesztés
                </button>
              </div>
            ) : undefined
          }
        >
          <div className="space-y-4">
            <p className="text-xs text-gray-400">
              {[
                `${viewing.items.length} blokk`,
                viewing.totalMinutes ? `${viewing.totalMinutes} perc` : null,
                viewing.ageGroupHint,
                viewing.createdByName,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            <PlanView goal={viewing.goal} items={viewing.items} />
            <p className="text-xs text-gray-400">
              Edzésre az esemény „Edzésterv” részénél, a „Sablon betöltése” gombbal lehet használni.
            </p>
          </div>
        </DrillSheet>
      )}

      {formOpen && (
        <TemplateFormModal
          template={editing}
          onClose={() => {
            setFormOpen(false);
            setEditing(null);
          }}
          onSaved={(template) => {
            setFormOpen(false);
            setEditing(null);
            showToast('Sablon elmentve.');
            load();
            setViewing(template);
          }}
        />
      )}

      <DeleteConfirmModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Sablon törlése"
        message="Biztosan törlöd ezt az edzéssablont? A belőle már betöltött edzéstervek megmaradnak."
        itemName={deleting?.name ?? ''}
      />
    </div>
  );
};

export default TemplatesTab;
